/**
 * Collaboration Panel - Shows accountants who have access to a report
 * Allows inviting new accountants and managing permissions
 * Includes case-level comments and activity feed
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/TestAuthContext';
import axios from 'axios';
import InviteAccountantModal from './InviteAccountantModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface Share {
  id: string;
  accountant_id?: string;
  accountant_name?: string;
  accountant_firm?: string;
  invite_email: string;
  invite_status: string;
  permissions: string[];
  shared_at: string;
  last_accessed?: string;
}

interface CaseComment {
  comment_id: string;
  case_id: string;
  user_id: string;
  user_name: string;
  text: string;
  created_at: string;
}

interface CaseActivity {
  case_id: string;
  user_id: string;
  user_name: string;
  action: string;
  details?: string;
  created_at: string;
}

type TabKey = 'collaborators' | 'comments' | 'activity';

interface CollaborationPanelProps {
  reportId: string;
}

export default function CollaborationPanel({ reportId }: CollaborationPanelProps) {
  const [shares, setShares] = useState<Share[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey>('collaborators');
  const { getToken } = useAuth();

  // Comments state
  const [comments, setComments] = useState<CaseComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  // Activity state
  const [activities, setActivities] = useState<CaseActivity[]>([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);

  const fetchShares = async () => {
    try {
      const token = await getToken();
      const response = await axios.get(
        `${BACKEND_URL}/api/v1/collaboration/reports/${reportId}/shares`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setShares(response.data);
    } catch (err) {
      console.error('Failed to fetch shares:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = useCallback(async () => {
    setCommentsLoading(true);
    try {
      const token = await getToken();
      const response = await axios.get(
        `${BACKEND_URL}/api/v1/collaboration/cases/${reportId}/comments`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setComments(response.data);
    } catch (err) {
      console.error('Failed to fetch comments:', err);
    } finally {
      setCommentsLoading(false);
    }
  }, [reportId, getToken]);

  const fetchActivity = useCallback(async () => {
    setActivitiesLoading(true);
    try {
      const token = await getToken();
      const response = await axios.get(
        `${BACKEND_URL}/api/v1/collaboration/cases/${reportId}/activity`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setActivities(response.data);
    } catch (err) {
      console.error('Failed to fetch activity:', err);
    } finally {
      setActivitiesLoading(false);
    }
  }, [reportId, getToken]);

  useEffect(() => {
    fetchShares();
  }, [reportId]);

  useEffect(() => {
    if (!isExpanded) return;
    if (activeTab === 'comments') {
      fetchComments();
    } else if (activeTab === 'activity') {
      fetchActivity();
    }
  }, [activeTab, isExpanded, fetchComments, fetchActivity]);

  const handleRevokeAccess = async (shareId: string) => {
    if (!window.confirm('Are you sure you want to revoke access for this accountant?')) {
      return;
    }

    try {
      const token = await getToken();
      await axios.delete(
        `${BACKEND_URL}/api/v1/collaboration/shares/${shareId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      // Refresh shares list
      fetchShares();
    } catch (err) {
      console.error('Failed to revoke access:', err);
      setErrorMessage('Failed to revoke access');
    }
  };

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setSubmittingComment(true);
    try {
      const token = await getToken();
      await axios.post(
        `${BACKEND_URL}/api/v1/collaboration/cases/${reportId}/comments`,
        { text: newComment.trim() },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setNewComment('');
      fetchComments();
    } catch (err) {
      console.error('Failed to submit comment:', err);
      setErrorMessage('Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const getPermissionBadges = (permissions: string[]) => {
    const badges = {
      view: { label: 'View', color: 'bg-blue-100 text-blue-800' },
      comment: { label: 'Comment', color: 'bg-green-100 text-green-800' },
      edit: { label: 'Edit', color: 'bg-yellow-100 text-yellow-800' },
      sign: { label: 'Sign', color: 'bg-blue-100 text-blue-800' }
    };

    return permissions.map((perm) => {
      const badge = badges[perm as keyof typeof badges];
      if (!badge) return null;
      return (
        <span
          key={perm}
          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}
        >
          {badge.label}
        </span>
      );
    });
  };

  const getActivityIcon = (action: string) => {
    switch (action) {
      case 'invite_sent':
        return (
          <svg className="w-4 h-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
        );
      case 'invite_revoked':
        return (
          <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        );
      case 'comment_added':
        return (
          <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        );
      default:
        return (
          <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  const formatTimeAgo = (dateStr: string) => {
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const tabs: { key: TabKey; label: string; count?: number }[] = [
    { key: 'collaborators', label: 'Collaborators', count: shares.length },
    { key: 'comments', label: 'Comments', count: comments.length },
    { key: 'activity', label: 'Activity' },
  ];

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-600 text-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <svg className="w-6 h-6 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <div>
              <h3 className="font-bold text-lg">Collaboration</h3>
              <p className="text-sm text-blue-100">
                {shares.length} {shares.length === 1 ? 'accountant' : 'accountants'} have access
              </p>
            </div>
          </div>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
          >
            <svg
              className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Content */}
      {isExpanded && (
        <div>
          {/* Error Message */}
          {errorMessage && (
            <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center justify-between">
              <span>{errorMessage}</span>
              <button onClick={() => setErrorMessage(null)} className="text-red-500 hover:text-red-700">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}

          {/* Tab Navigation */}
          <div className="border-b border-gray-200 px-6">
            <nav className="flex space-x-6" aria-label="Tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.key
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                  {tab.count !== undefined && tab.count > 0 && (
                    <span className={`ml-1.5 inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      activeTab === tab.key ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* ====== COLLABORATORS TAB ====== */}
            {activeTab === 'collaborators' && (
              <>
                {loading ? (
                  <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <p className="text-gray-600 mt-2">Loading...</p>
                  </div>
                ) : (
                  <>
                    {/* Invite Button */}
                    <button
                      onClick={() => setShowInviteModal(true)}
                      className="w-full mb-4 px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-600 text-white rounded-lg hover:from-blue-700 hover:to-blue-700 transition-all font-medium flex items-center justify-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Invite Accountant
                    </button>

                    {/* Shares List */}
                    {shares.length === 0 ? (
                      <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
                        <svg className="w-12 h-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                        </svg>
                        <p className="text-gray-600 font-medium">No accountants invited yet</p>
                        <p className="text-sm text-gray-500 mt-1">Invite a forensic accountant to collaborate</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {shares.map((share) => (
                          <div
                            key={share.id}
                            className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-500 flex items-center justify-center text-white font-bold">
                                    {share.accountant_name ? share.accountant_name.charAt(0).toUpperCase() : share.invite_email.charAt(0).toUpperCase()}
                                  </div>
                                  <div>
                                    <h4 className="font-semibold text-gray-900">
                                      {share.accountant_name || share.invite_email}
                                    </h4>
                                    {share.accountant_firm && (
                                      <p className="text-sm text-gray-600">{share.accountant_firm}</p>
                                    )}
                                  </div>
                                </div>

                                {/* Status */}
                                <div className="flex items-center gap-2 mt-2 mb-2">
                                  {share.invite_status === 'pending' ? (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                      </svg>
                                      Pending Acceptance
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                      </svg>
                                      Active
                                    </span>
                                  )}
                                </div>

                                {/* Permissions */}
                                <div className="flex flex-wrap gap-1 mb-2">
                                  {getPermissionBadges(share.permissions)}
                                </div>

                                {/* Timestamps */}
                                <div className="text-xs text-gray-500">
                                  <p>Invited {new Date(share.shared_at).toLocaleDateString()}</p>
                                  {share.last_accessed && (
                                    <p>Last active {new Date(share.last_accessed).toLocaleDateString()}</p>
                                  )}
                                </div>
                              </div>

                              {/* Revoke Button */}
                              <button
                                onClick={() => handleRevokeAccess(share.id)}
                                className="text-red-600 hover:bg-red-50 p-2 rounded-lg transition-colors"
                                title="Revoke Access"
                              >
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            {/* ====== COMMENTS TAB ====== */}
            {activeTab === 'comments' && (
              <>
                {/* New Comment Form */}
                <form onSubmit={handleSubmitComment} className="mb-4">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Add a comment..."
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      disabled={submittingComment}
                    />
                    <button
                      type="submit"
                      disabled={submittingComment || !newComment.trim()}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 text-sm font-medium"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                      Send
                    </button>
                  </div>
                </form>

                {/* Comments List */}
                {commentsLoading ? (
                  <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    <p className="text-gray-500 mt-2 text-sm">Loading comments...</p>
                  </div>
                ) : comments.length === 0 ? (
                  <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
                    <svg className="w-10 h-10 mx-auto text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <p className="text-gray-600 font-medium text-sm">No comments yet</p>
                    <p className="text-xs text-gray-500 mt-1">Start the conversation</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-80 overflow-y-auto">
                    {comments.map((comment) => (
                      <div key={comment.comment_id} className="flex gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                          {comment.user_name.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-2">
                            <span className="font-medium text-gray-900 text-sm truncate">
                              {comment.user_name}
                            </span>
                            <span className="text-xs text-gray-400 flex-shrink-0">
                              {formatTimeAgo(comment.created_at)}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700 mt-0.5 break-words">
                            {comment.text}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* ====== ACTIVITY TAB ====== */}
            {activeTab === 'activity' && (
              <>
                {activitiesLoading ? (
                  <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    <p className="text-gray-500 mt-2 text-sm">Loading activity...</p>
                  </div>
                ) : activities.length === 0 ? (
                  <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
                    <svg className="w-10 h-10 mx-auto text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-gray-600 font-medium text-sm">No activity yet</p>
                    <p className="text-xs text-gray-500 mt-1">Collaboration activity will appear here</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-80 overflow-y-auto">
                    {activities.map((activity, idx) => (
                      <div key={idx} className="flex gap-3 items-start">
                        <div className="mt-0.5 flex-shrink-0">
                          {getActivityIcon(activity.action)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-700">
                            <span className="font-medium text-gray-900">{activity.user_name}</span>
                            {' '}
                            <span className="text-gray-500">
                              {activity.action.replace(/_/g, ' ')}
                            </span>
                          </p>
                          {activity.details && (
                            <p className="text-xs text-gray-500 mt-0.5 truncate">{activity.details}</p>
                          )}
                          <p className="text-xs text-gray-400 mt-0.5">
                            {formatTimeAgo(activity.created_at)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Invite Modal */}
      <InviteAccountantModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        reportId={reportId}
        onInviteSent={fetchShares}
      />
    </div>
  );
}
